import React, { memo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Tooltip } from 'antd';

import { PATHS } from 'router/config';
import {
  useGroupMessagesFeedQuery,
  useGroupConversationsListQuery,
  useMarkMsgReadMutation,
  useFlagMsgMutation,
  useSetMsgHandledMutation,
} from 'core/graphql';
import { Colors } from 'services/ColorService';

import {
  TaskFromMessageModal,
  ITaskSourceMessage,
} from './TaskFromMessageModal';
import { AuthorAvatar } from './AuthorAvatar';

interface ISlackMessagesPanelProps {
  groupId: string;
  /** Preselect the per-channel filter (dashboard `?conv=` deep link). */
  initialChannelId?: string;
}

// Compact by default: a short first page keeps the card scannable, "show more"
// then pulls proper pages.
const FIRST_PAGE = 5;
const NEXT_PAGE = 20;

/**
 * Unified newest-first Slack feed for a group (workspace-per-group rework):
 * every channel + its students' DMs in one keyset-paginated stream, with search
 * / only-unread / only-flagged / per-channel filters and per-message read +
 * flag toggles (team-wide global state). Replaces the old per-channel
 * conversations panel in the group card.
 */
export const SlackMessagesPanel = memo(
  ({ groupId, initialChannelId }: ISlackMessagesPanelProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const [search, setSearch] = useState('');
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [flaggedOnly, setFlaggedOnly] = useState(false);
    const [unhandledOnly, setUnhandledOnly] = useState(false);
    const [channelId, setChannelId] = useState(initialChannelId || '');
    const [taskMessage, setTaskMessage] = useState<ITaskSourceMessage | null>(
      null,
    );

    const { data, loading, fetchMore, refetch } = useGroupMessagesFeedQuery({
      variables: {
        groupId,
        first: FIRST_PAGE,
        search: search.trim() || undefined,
        unreadOnly,
        flaggedOnly,
        unhandledOnly,
        channelId: channelId || undefined,
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });
    const edges = data?.groupMessages?.edges || [];
    const pageInfo = data?.groupMessages?.pageInfo;

    const { data: convData } = useGroupConversationsListQuery({
      variables: { groupId },
      fetchPolicy: 'cache-and-network',
    });
    const channels = (convData?.groupConversations || []).filter(
      (c) => c?.kind === 'slack_channel',
    );

    const [markRead] = useMarkMsgReadMutation();
    const [flag] = useFlagMsgMutation();
    const [setHandled] = useSetMsgHandledMutation();

    // Appending is done by the `groupMessages` relayStylePagination cache
    // policy (core/cache) — no manual `updateQuery` concatenation, so a page
    // of an old filter can never be written into the new filter's list.
    const loadMore = useCallback(() => {
      if (!pageInfo?.hasNextPage) return;
      fetchMore({
        variables: { after: pageInfo.endCursor, first: NEXT_PAGE },
      }).catch(() => {});
    }, [fetchMore, pageInfo]);

    const openTasksTab = useCallback(() => {
      history.push(`${PATHS.groupDetail.replace(':id', groupId)}?tab=tasks`);
    }, [history, groupId]);

    return (
      <Card>
        <CardTitle>{t('pages.communications.feedTitle')}</CardTitle>

        <Filters>
          <SearchInput
            type="search"
            value={search}
            placeholder={t('pages.communications.searchMessages')}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            <option value="">{t('pages.communications.allChannels')}</option>
            {channels.map((c) => (
              <option key={c?.id} value={c?.id}>
                {c?.title || c?.externalId}
              </option>
            ))}
          </Select>
          <Toggle>
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            {t('pages.communications.onlyUnread')}
          </Toggle>
          <Toggle>
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
            />
            {t('pages.communications.onlyFlagged')}
          </Toggle>
          <Toggle>
            <input
              type="checkbox"
              checked={unhandledOnly}
              onChange={(e) => setUnhandledOnly(e.target.checked)}
            />
            {t('pages.communications.unhandledOnly')}
          </Toggle>
        </Filters>

        {loading && edges.length === 0 ? (
          <Muted>{t('pages.communications.loading')}</Muted>
        ) : edges.length === 0 ? (
          <Muted>{t('pages.communications.empty')}</Muted>
        ) : (
          <List>
            {edges.map((edge) => {
              const m = edge?.node;
              if (!m) return null;
              const inbound = m.direction === 'inbound';
              return (
                <Item key={m.id} unread={!m.isRead} handled={!!m.isHandled}>
                  <AuthorAvatar name={m.authorDisplay} />
                  <ItemMain>
                    <Head>
                      <Author>{m.authorDisplay || '—'}</Author>
                      {m.kind === 'slack_dm' ? (
                        <KindPill tone="slack">
                          {t('pages.communications.dmShort')} · {m.channelName}
                        </KindPill>
                      ) : (
                        <KindPill tone="channel">#{m.channelName}</KindPill>
                      )}
                      <Tooltip
                        title={moment(m.sentAt).format('DD.MM.YYYY HH:mm:ss')}
                      >
                        <Time>{moment(m.sentAt).format('DD.MM HH:mm')}</Time>
                      </Tooltip>
                      {!m.isRead ? (
                        <Dot title={t('pages.communications.unread')} />
                      ) : null}
                      {m.isHandled ? (
                        <HandledChip>
                          ✓ {t('pages.communications.handledShort')}
                        </HandledChip>
                      ) : null}
                      {m.linkedTask ? (
                        <TaskChip
                          type="button"
                          status={m.linkedTask.status}
                          title={m.linkedTask.title}
                          onClick={openTasksTab}
                        >
                          {t('pages.communications.taskChip')}:{' '}
                          {m.linkedTask.title}
                        </TaskChip>
                      ) : null}
                    </Head>
                    <Body>{m.body}</Body>
                    <Actions>
                      <LinkBtn
                        type="button"
                        onClick={() =>
                          markRead({
                            variables: { messageId: m.id, read: !m.isRead },
                          })
                        }
                      >
                        {m.isRead
                          ? t('pages.communications.markUnread')
                          : t('pages.communications.markRead')}
                      </LinkBtn>
                      <LinkBtn
                        type="button"
                        active={!!m.isFlagged}
                        onClick={() =>
                          flag({
                            variables: {
                              messageId: m.id,
                              flagged: !m.isFlagged,
                            },
                          })
                        }
                      >
                        {m.isFlagged
                          ? t('pages.communications.unflag')
                          : t('pages.communications.flag')}
                      </LinkBtn>
                      <LinkBtn
                        type="button"
                        done={!!m.isHandled}
                        onClick={() => {
                          const handled = !m.isHandled;
                          setHandled({
                            variables: { messageId: m.id, handled },
                          })
                            .then(() => {
                              // With the "only unhandled" filter on, drop the
                              // now-handled row from the list. Un-handling is
                              // NOT refetched so a misclick can be undone.
                              if (handled && unhandledOnly) {
                                refetch().catch(() => {});
                              }
                            })
                            .catch(() => {});
                        }}
                      >
                        {m.isHandled
                          ? t('pages.communications.markUnhandled')
                          : t('pages.communications.markHandled')}
                      </LinkBtn>
                      {inbound && !m.linkedTask ? (
                        <LinkBtn
                          type="button"
                          onClick={() =>
                            setTaskMessage({
                              id: m.id,
                              body: m.body,
                              authorName: m.authorDisplay || m.authorName,
                              channelName: m.channelName,
                              kind: m.kind,
                              sentAt: m.sentAt,
                            })
                          }
                        >
                          {t('pages.communications.createTask')}
                        </LinkBtn>
                      ) : null}
                    </Actions>
                  </ItemMain>
                </Item>
              );
            })}
          </List>
        )}

        {pageInfo?.hasNextPage ? (
          <MoreButton type="button" disabled={loading} onClick={loadMore}>
            {t('pages.communications.loadMore')}
          </MoreButton>
        ) : null}

        {taskMessage ? (
          <TaskFromMessageModal
            message={taskMessage}
            groupId={groupId}
            onClose={() => setTaskMessage(null)}
            onCreated={() => {
              // The mutation payload updates the message in cache; refetch the
              // current page so unhandled-only filtering stays correct.
              refetch().catch(() => {});
            }}
          />
        ) : null}
      </Card>
    );
  },
);

SlackMessagesPanel.displayName = 'SlackMessagesPanel';

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 160px;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const Select = styled.select`
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 7px 8px;
  font: inherit;
  font-size: 13px;
  background: ${Colors.White};
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${Colors.Gray};
  cursor: pointer;
  white-space: nowrap;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
  padding: 8px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.div<{ unread: boolean; handled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid ${Colors.SecondLightGray};
  border-left: 3px solid
    ${({ unread, handled }) =>
      handled ? '#10b981' : unread ? Colors.Blue : Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  opacity: ${({ handled }) => (handled ? 0.7 : 1)};
  background: ${Colors.White};
`;

const ItemMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const HandledChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #047857;
  background: #d1fae5;
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
`;

const TaskChip = styled.button<{ status?: string | null }>`
  border: 0;
  font: inherit;
  cursor: pointer;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
  color: ${({ status }) =>
    status === 'done'
      ? '#047857'
      : status === 'in_progress'
      ? '#1e3a8a'
      : '#475569'};
  background: ${({ status }) =>
    status === 'done'
      ? '#d1fae5'
      : status === 'in_progress'
      ? '#dbeafe'
      : '#f1f5f9'};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
`;

const Author = styled.span`
  font-weight: 700;
  color: ${Colors.Black};
`;

const KindPill = styled.span<{ tone: 'slack' | 'channel' }>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
  color: ${({ tone }) => (tone === 'slack' ? '#5b21b6' : '#475569')};
  background: ${({ tone }) => (tone === 'slack' ? '#ede9fe' : '#f1f5f9')};
`;

const Time = styled.span`
  color: ${Colors.Gray};
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${Colors.Blue};
  display: inline-block;
`;

const Body = styled.div`
  font-size: 13px;
  color: ${Colors.Black};
  margin-top: 4px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 6px;
`;

const LinkBtn = styled.button<{ active?: boolean; done?: boolean }>`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  color: ${({ active, done }) =>
    done ? '#047857' : active ? Colors.WarningDefault : Colors.Blue};
  font-weight: ${({ active, done }) => (active || done ? 700 : 400)};
`;

const MoreButton = styled.button`
  margin-top: 12px;
  width: 100%;
  border: 1px solid ${Colors.SecondLightGray};
  background: ${Colors.White};
  border-radius: 8px;
  padding: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Blue};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
